import React, { useState, useEffect } from 'react';
import { markUpgradePopupSeen, getVIPBenefits } from '../services/vipUpgradeService';

function VIPUpgradePopup({ 
  isOpen, 
  onClose, 
  userId,
  eligibility,
  onUpgradeSuccess,
  onOpenVIPLevels
}) {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [reminderOption, setReminderOption] = useState(null);

  useEffect(() => {
    if (isOpen && eligibility) {
      setError('');
      setReminderOption(null);
    }
  }, [isOpen, eligibility]);

  if (!isOpen || !eligibility || !eligibility.eligible) {
    return null;
  }

  // Use upgradePrice (new system) or fallback to upgradeCost (old system) for compatibility
  const upgradePrice = eligibility.upgradePrice || eligibility.upgradeCost || 0;
  const currentBalance = eligibility.currentBalance || 0;
  const newBalance = eligibility.newBalance || currentBalance;
  const { currentVIP, nextVIP } = eligibility;
  const benefits = getVIPBenefits(currentVIP, nextVIP);

  const handleUpgradeClick = async () => {
    // Do not upgrade from the user popup; only navigate to VIP Levels.
    setProcessing(true);
    setError('');

    try {
      await markUpgradePopupSeen(userId, nextVIP.replace('VIP', ''), 'viewed');

      // Close popup
      onClose();

      // Ask parent to open VIP Levels page
      if (onOpenVIPLevels) {
        onOpenVIPLevels();
      }

      // Optional callback for analytics
      if (onUpgradeSuccess) {
        onUpgradeSuccess({ success: true, redirected: true });
      }
    } catch (err) {
      console.error('Error handling VIP upgrade navigation:', err);
      setError(err.message || 'Failed to open VIP levels page');
    } finally {
      setProcessing(false);
    }
  };

  const handleRemindLater = async (option = '24hours') => {
    try {
      await markUpgradePopupSeen(userId, nextVIP.replace('VIP', ''), option);
      onClose();
    } catch (err) {
      console.error('Error marking popup as seen:', err);
    }
  };

  const handleSkip = async () => {
    await handleRemindLater('skip');
  };

  const vipColors = {
    'VIP1': { bg: '#3b82f6', badge: '🟦' },
    'VIP2': { bg: '#10b981', badge: '🟩' },
    'VIP3': { bg: '#a855f7', badge: '🟪' },
    'VIP4': { bg: '#f59e0b', badge: '🟨' }
  };

  const currentColor = vipColors[currentVIP] || vipColors['VIP1'];
  const nextColor = vipColors[nextVIP] || vipColors['VIP2'];

  return (
    <div className="vip-upgrade-overlay" onClick={handleSkip}>
      <div className="vip-upgrade-popup" onClick={(e) => e.stopPropagation()}>
        <div className="vip-upgrade-header" style={{ background: `linear-gradient(135deg, ${currentColor.bg}, ${nextColor.bg})` }}>
          <button 
            className="vip-upgrade-close"
            onClick={handleSkip}
            disabled={processing}
          >
            <i className="bi bi-x-lg"></i>
          </button>
          <div className="text-center text-white">
            <div className="vip-upgrade-icon mb-2">
              <i className="bi bi-star-fill" style={{ fontSize: '3rem' }}></i>
            </div>
            <h2 className="mb-0">🎉 VIP Upgrade Available!</h2>
            <p className="mb-0 mt-2">Congratulations! You're eligible for an upgrade</p>
          </div>
        </div>

        <div className="vip-upgrade-body">
          {error && (
            <div className="alert alert-danger">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </div>
          )}

          {/* Current vs Next VIP */}
          <div className="vip-comparison mb-4">
            <div className="row text-center">
              <div className="col-6">
                <div className="vip-level-card" style={{ borderColor: currentColor.bg }}>
                  <div className="vip-badge mb-2">{currentColor.badge}</div>
                  <h4 className="mb-1">{currentVIP}</h4>
                  <div className="vip-stats">
                    <div className="small text-muted">
                      {benefits.from.sets} sets × {benefits.from.tasksPerSet} tasks
                    </div>
                    <div className="small text-muted">
                      {benefits.from.totalTasks} total daily tasks
                    </div>
                    <div className="small text-muted">
                      {benefits.from.commission}% commission
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6">
                <div className="vip-level-card" style={{ borderColor: nextColor.bg }}>
                  <div className="vip-badge mb-2">{nextColor.badge}</div>
                  <h4 className="mb-1">{nextVIP}</h4>
                  <div className="vip-stats">
                    <div className="small text-success">
                      {benefits.to.sets} sets × {benefits.to.tasksPerSet} tasks
                    </div>
                    <div className="small text-success">
                      {benefits.to.totalTasks} total daily tasks
                    </div>
                    <div className="small text-success">
                      {benefits.to.commission}% commission
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className="vip-benefits mb-4">
            <h5 className="mb-3">
              <i className="bi bi-gift me-2"></i>
              Upgrade Benefits:
            </h5>
            <ul className="list-unstyled">
              <li className="mb-2">
                <i className="bi bi-check-circle-fill text-success me-2"></i>
                <strong>+{benefits.improvements.setsIncrease}</strong> more sets ({benefits.from.sets} → {benefits.to.sets})
              </li>
              <li className="mb-2">
                <i className="bi bi-check-circle-fill text-success me-2"></i>
                <strong>+{benefits.improvements.tasksPerSetIncrease}</strong> tasks per set ({benefits.from.tasksPerSet} → {benefits.to.tasksPerSet})
              </li>
              <li className="mb-2">
                <i className="bi bi-check-circle-fill text-success me-2"></i>
                <strong>+{benefits.improvements.totalTasksIncrease}</strong> total daily tasks ({benefits.from.totalTasks} → {benefits.to.totalTasks})
              </li>
              <li className="mb-2">
                <i className="bi bi-check-circle-fill text-success me-2"></i>
                <strong>+{benefits.improvements.earningPotentialIncrease}%</strong> more earning potential
              </li>
              <li className="mb-2">
                <i className="bi bi-check-circle-fill text-success me-2"></i>
                Higher commission rate ({benefits.from.commission}% → {benefits.to.commission}%)
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="vip-upgrade-actions">
            <button
              className="btn btn-primary btn-lg w-100 mb-2"
              onClick={handleUpgradeClick}
              disabled={processing}
              style={{ 
                background: `linear-gradient(135deg, ${currentColor.bg}, ${nextColor.bg})`,
                border: 'none'
              }}
            >
              {processing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Processing Upgrade...
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-up-circle me-2"></i>
                  Upgrade to {nextVIP} Now
                </>
              )}
            </button>
            
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary flex-fill"
                onClick={() => handleRemindLater('24hours')}
                disabled={processing}
              >
                <i className="bi bi-clock me-2"></i>
                Remind in 24h
              </button>
              <button
                className="btn btn-outline-secondary flex-fill"
                onClick={handleSkip}
                disabled={processing}
              >
                <i className="bi bi-x-circle me-2"></i>
                Skip for Now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VIPUpgradePopup;

